import type { Atom } from 'jotai';
import { atom, getDefaultStore } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import type { FileTemplateRecord } from '../_graphql/queryFileTemplates';
import {
  fileTemplatesQueryAtom,
  FileTemplateType,
} from '../_graphql/queryFileTemplates';

import { fileGenerateAccessUrlMutationAtom } from '~/modules/files/_graphql/mutations/fileGenerateAccessUrlMutation';
import type { QueryAtomReadValue } from '~/shared/providers/api/createQueryAtomFactory';

export const $generateFileAccessUrl = fileGenerateAccessUrlMutationAtom();

type TemplateCategory = {
  category: string;
  types: { key: FileTemplateType; label: string }[];
};

export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    category: 'Finance',
    types: [
      { key: 'ApSettlementReport', label: 'AP Settlement Report' },
      { key: 'BillAp', label: 'Bill (AP)' },
      { key: 'CheckAp', label: 'Check (AP)' },
      { key: 'InvoiceAr', label: 'Invoice (AR)' },
    ],
  },
  {
    category: 'Inventory',
    types: [{ key: 'InventoryTag', label: 'Inventory Tag' }],
  },
  {
    category: 'Pricing',
    types: [{ key: 'PriceList', label: 'Price List' }],
  },
  {
    category: 'Purchases',
    types: [
      { key: 'FullPageTicket', label: 'Full Page Ticket' },
      { key: 'FullPageWeightTicket', label: 'Full Page Weight Ticket' },
      { key: 'PurchaseOrder', label: 'Purchase Order' },
      { key: 'ThermalTicket', label: 'Thermal Ticket' },
      { key: 'ThermalWeightTicket', label: 'Thermal Weight Ticket' },
    ],
  },
  {
    category: 'Sales',
    types: [
      { key: 'BillOfLading', label: 'Bill of Lading' },
      { key: 'BookingInvoice', label: 'Booking Invoice' },
      { key: 'LoadInvoice', label: 'Load Invoice' },
      { key: 'OutboundTicket', label: 'Outbound Ticket' },
      { key: 'PackingList', label: 'Packing List' },
      { key: 'SalesOrder', label: 'Sales Order' },
      { key: 'ShippingSummary', label: 'Shipping Summary' },
    ],
  },
];

export const TEMPLATE_TYPE_OPTIONS = TEMPLATE_CATEGORIES.flatMap((c) =>
  c.types.map((t) => ({ key: t.key, label: t.label })),
);

export const TEMPLATE_TYPE_TO_SETTING_ID: Record<FileTemplateType, string> = {
  ThermalTicket: 'purchases.thermalTicket.templateFileId',
  FullPageTicket: 'purchases.fullPageTicket.templateFileId',
  FullPageWeightTicket: 'purchases.fullPageWeightTicket.templateFileId',
  ThermalWeightTicket: 'purchases.thermalWeightTicket.templateFileId',
  PurchaseOrder: 'trade.purchaseOrder.templateFileId',
  OutboundTicket: 'sales.outboundTicket.templateFileId',
  SalesOrder: 'sales.salesOrder.templateFileId',
  ShippingSummary: 'sales.shippingSummary.templateFileId',
  BookingInvoice: 'sales.bookingInvoice.templateFileId',
  LoadInvoice: 'sales.loadInvoice.templateFileId',
  PackingList: 'sales.packingList.templateFileId',
  BillOfLading: 'sales.billOfLading.templateFileId',
  InventoryTag: 'inventory.inventoryTag.templateFileId',
  ApSettlementReport: 'finance.apSettlementReport.templateFileId',
  CheckAp: 'finance.checkAp.templateFileId',
  BillAp: 'finance.billAp.templateFileId',
  InvoiceAr: 'finance.invoiceAr.templateFileId',
  PriceList: 'pricing.priceList.templateFileId',
};

export const $selectedTemplateType =
  atom<FileTemplateType>('ApSettlementReport');

export const $allFileTemplates = fileTemplatesQueryAtom(() => ({
  variables: {},
}));

export const $fileTemplates: Atom<QueryAtomReadValue<FileTemplateRecord[]>> =
  atom((get) => {
    const allTemplates = get($allFileTemplates);
    const selectedType = get($selectedTemplateType);

    const filteredData = allTemplates.data?.filter(
      (template) => template.type === selectedType,
    );

    return {
      ...allTemplates,
      data: filteredData,
    };
  });

const $explicitSelectedTemplateId = atom<string | undefined>(undefined);

const $defaultTemplateIdForCurrentType = atom((get) => {
  const selectedType = get($selectedTemplateType);
  const allTemplates = get($allFileTemplates);
  return allTemplates.data?.find((t) => t.type === selectedType && t.isDefault)
    ?.id;
});

export const $selectedTemplateId = atom(
  (get) => {
    const explicit = get($explicitSelectedTemplateId);
    if (explicit !== undefined) return explicit;
    return get($defaultTemplateIdForCurrentType);
  },
  (_get, set, value: string | undefined) => {
    set($explicitSelectedTemplateId, value);
  },
);

export const $explicitSavedTemplateIds = atom<
  Partial<Record<FileTemplateType, string | undefined>>
>({});

const $defaultTemplateIds = atom((get) => {
  const allTemplates = get($allFileTemplates);
  const result: Record<FileTemplateType, string | undefined> = {
    ThermalTicket: undefined,
    FullPageTicket: undefined,
    FullPageWeightTicket: undefined,
    ThermalWeightTicket: undefined,
    PurchaseOrder: undefined,
    OutboundTicket: undefined,
    SalesOrder: undefined,
    ShippingSummary: undefined,
    BookingInvoice: undefined,
    LoadInvoice: undefined,
    PackingList: undefined,
    BillOfLading: undefined,
    InventoryTag: undefined,
    ApSettlementReport: undefined,
    CheckAp: undefined,
    BillAp: undefined,
    InvoiceAr: undefined,
    PriceList: undefined,
  };

  for (const template of allTemplates.data ?? []) {
    if (template.isDefault) {
      result[template.type as FileTemplateType] = template.id;
    }
  }

  return result;
});

export const $savedTemplateIds = atom((get) => {
  const defaults = get($defaultTemplateIds);
  const explicit = get($explicitSavedTemplateIds);

  return {
    ...defaults,
    ...explicit,
  };
});

export const $currentSavedTemplateId = atom((get) => {
  const selectedType = get($selectedTemplateType);
  const savedTemplateIds = get($savedTemplateIds);
  return savedTemplateIds[selectedType];
});

export const $hasUnsavedChanges = atom((get) => {
  const selectedTemplateId = get($selectedTemplateId);
  const currentSavedTemplateId = get($currentSavedTemplateId);
  return selectedTemplateId !== currentSavedTemplateId;
});

export const $resetTemplateSelection = atom(null, (get, set) => {
  const currentSavedTemplateId = get($currentSavedTemplateId);
  set($selectedTemplateId, currentSavedTemplateId);
});

export const $selectTemplateType = atom(
  null,
  (_get, set, templateType: FileTemplateType) => {
    set($selectedTemplateType, templateType);
    set($explicitSelectedTemplateId, undefined);
  },
);

export const $selectedTemplate = atom((get) => {
  const fileTemplates = get($fileTemplates);
  const selectedTemplateId = get($selectedTemplateId);
  return fileTemplates.data?.find((t) => t.id === selectedTemplateId);
});

export const $templateOptions = atom((get) => {
  const fileTemplates = get($fileTemplates);
  return (
    fileTemplates.data?.map((template) => ({
      key: template.id,
      label: template.description,
    })) ?? []
  );
});

export const $templatePreviewQuery = atomWithQuery((get) => {
  const selectedTemplate = get($selectedTemplate);
  const fileId = selectedTemplate?.fileId;

  return {
    queryKey: ['templatePreview', fileId],
    queryFn: async () => {
      if (!fileId) return undefined;

      const store = getDefaultStore();
      const result = await store.set($generateFileAccessUrl, {
        fileIds: [fileId],
        expirySeconds: 3600,
      });
      return result.filesGenerateFileAccessURL?.results?.[0]?.url ?? undefined;
    },
    enabled: !!fileId,
  };
});

export const $isSaving = atom(false);
